/**
 * FCM Token Manager
 * ─────────────────────────────────────────────────────────────────────────────
 * Handles: request permission → get token → save to Firestore → foreground msg
 *
 * Usage:
 *   import { initFCM, removeFCMToken } from './fcmToken';
 *   await initFCM(user);   // call after user logs in
 *   await removeFCMToken(user);  // call on logout (optional)
 */

import { db, messaging, getToken, onMessage, updateDoc, doc, setDoc, arrayUnion, arrayRemove } from '../firebase';

const VAPID_KEY = process.env.REACT_APP_VAPID_KEY;

// ─── Callable gate ───────────────────────────────────────────────────────────
// WHY: the Functions SDK attaches this device's FCM token to EVERY callable by calling the
// messaging getToken() WITHOUT a VAPID key. If that runs before getFCMToken() below has passed
// VAPID_KEY (initFCM waits 1 s first), the SDK falls back to Firebase's default key and
// re-creates the token: the one saved in users.fcmTokens is deleted at FCM (a dead token every
// later push still pays for), then initFCM re-creates it once more. Callables that fire on their
// own — not from a user click — await this first (recordVisit, job-board warmups).
let vapidApplied = false;
let resolveVapidApplied;
const vapidAppliedPromise = new Promise((r) => { resolveVapidApplied = r; });
function markVapidApplied() {
    if (vapidApplied) return;
    vapidApplied = true;
    resolveVapidApplied();
}

// Resolves when a callable can no longer re-key the token: notifications not granted (the SDK
// attaches no token), our key already applied, or `timeoutMs` passed (FCM unsupported / never inits).
export function whenSafeForCallables(timeoutMs = 10000) {
    try {
        if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return Promise.resolve();
    } catch (_) {
        return Promise.resolve();
    }
    if (vapidApplied) return Promise.resolve();
    return Promise.race([vapidAppliedPromise, new Promise((r) => setTimeout(r, timeoutMs))]);
}

// ─── Request notification permission ─────────────────────────────────────────
export async function requestNotificationPermission() {
    if (typeof Notification === 'undefined') return 'unsupported';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';

    try {
        const permission = await Notification.requestPermission();
        return permission;
    } catch (err) {
        console.error('[FCM] Permission request failed:', err);
        return 'denied';
    }
}

// ─── Get FCM token ────────────────────────────────────────────────────────────
async function getFCMToken() {
    if (!messaging) {
        console.warn('[FCM] Messaging not initialized yet');
        return null;
    }
    if (!VAPID_KEY) {
        console.warn('[FCM] REACT_APP_VAPID_KEY not set in .env — skipping FCM token');
        markVapidApplied(); // no key of ours to protect
        return null;
    }

    try {
        const token = await getToken(messaging, {
            vapidKey: VAPID_KEY,
            serviceWorkerRegistration: await navigator.serviceWorker.getRegistration('/'),
        });
        return token || null;
    } catch (err) {
        console.warn('[FCM] getToken failed:', err.message);
        return null;
    } finally {
        markVapidApplied(); // messaging now holds our VAPID key + SW registration
    }
}

// ─── Save token to Firestore ──────────────────────────────────────────────────
async function saveTokenToFirestore(userId, token) {
    if (!userId || !token || !db) return;

    try {
        const userRef = doc(db, 'users', userId);

        // Atomic union so concurrent devices don't clobber each other's tokens.
        // arrayUnion also de-dupes, so no explicit duplicate check is needed.
        // Note: the previous max-5 cap is dropped in favor of correctness.
        await updateDoc(userRef, { fcmTokens: arrayUnion(token) });

        console.log('[FCM] Token saved to Firestore ✅');
    } catch (err) {
        console.error('[FCM] Failed to save token:', err);
    }
}

// ─── Remove token from Firestore (on logout) ──────────────────────────────────
export async function removeFCMToken(userId) {
    if (!userId || !messaging || !db) return;

    try {
        const token = await getFCMToken();
        if (!token) return;

        const userRef = doc(db, 'users', userId);

        // Atomic removal so we don't clobber tokens added by other devices concurrently.
        await updateDoc(userRef, { fcmTokens: arrayRemove(token) });

        console.log('[FCM] Token removed from Firestore ✅');
    } catch (err) {
        console.error('[FCM] Failed to remove token:', err);
    }
}

// ─── Initialize FCM for a logged-in user ─────────────────────────────────────
export async function initFCM(user, onForegroundMessage) {
    if (!user || user.isAnonymous) return;
    if (!('serviceWorker' in navigator)) return;

    try {
        // 1. Request permission
        const permission = await requestNotificationPermission();
        if (permission !== 'granted') {
            console.log('[FCM] Notification permission:', permission);
            return;
        }

        // 2. Wait a bit for messaging to be initialized
        await new Promise((r) => setTimeout(r, 1000));

        // 3. Get FCM token
        const token = await getFCMToken();
        if (!token) return;

        console.log('[FCM] Got token:', token.substring(0, 20) + '…');

        // 4. Save to Firestore
        await saveTokenToFirestore(user.uid, token);

        // 5. Listen for foreground messages
        if (messaging && onForegroundMessage) {
            const unsubscribe = onMessage(messaging, (payload) => {
                console.log('[FCM] Foreground message:', payload);
                onForegroundMessage(payload);
            });
            return unsubscribe;
        }
    } catch (err) {
        console.error('[FCM] initFCM error:', err);
    }
}
